<template>
    <div class="campaign-detail-container">
        <o-bread-crumb>
            <template v-slot:pageName>Campaign</template>
            <template v-slot:pageDetail>{{ campaign?.name || 'Detail' }}</template>
            <template v-slot:pageDescription>
                <button class="btn-back" @click="goBack">
                    <i class="fas fa-arrow-left"></i> Back
                </button>
            </template>
        </o-bread-crumb>

        <!-- Feature Disabled State -->
        <div v-if="!isMarketingModuleEnabled" class="feature-disabled-container">
            <div class="disabled-content">
                <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
                <h4>Marketing Module Disabled</h4>
                <p>The Marketing Module has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
            </div>
        </div>

        <template v-if="isMarketingModuleEnabled">
        <div v-if="validation" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle me-2"></i>{{ validation }}
        </div>

        <div v-if="page.isLoading" class="loading-state">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="loading-text">Loading campaign details...</p>
        </div>

        <div v-else-if="campaign" class="detail-content">
            <!-- Campaign Info Card -->
            <div class="info-card">
                <div class="info-header">
                    <div class="info-header-left">
                        <i class="fas fa-rocket"></i>
                        <h5>Campaign Information</h5>
                    </div>
                </div>
                <div class="info-body">
                    <!-- Main Info Grid - 3 Columns -->
                    <div class="info-grid-compact">
                        <div class="info-item-compact">
                            <label><i class="fas fa-rocket"></i> Campaign Name</label>
                            <div class="info-value-compact">{{ campaign.name }}</div>
                        </div>
                        <div class="info-item-compact">
                            <label><i class="fas fa-bullhorn"></i> Marketing</label>
                            <div class="info-value-compact">{{ campaign.marketing?.name || '-' }}</div>
                        </div>
                        <div class="info-item-compact">
                            <label><i class="fas fa-tag"></i> Business Type</label>
                            <span class="badge-type-compact">{{ getBusinessTypeName(campaign.businessable_type) }}</span>
                        </div>
                        <div class="info-item-compact">
                            <label><i class="fas fa-briefcase"></i> Business</label>
                            <div class="info-value-compact">{{ campaign.businessable?.name || '-' }}</div>
                        </div>
                        <div class="info-item-compact">
                            <label><i class="fas fa-calendar"></i> Validity Period</label>
                            <div class="info-value-compact">{{ formatDate(campaign.validity_start) }} - {{ formatDate(campaign.validity_end) }}</div>
                        </div>
                        <div class="info-item-compact">
                            <label><i class="fas fa-clock"></i> Created At</label>
                            <div class="info-value-compact">{{ formatDate(campaign.created_at) }}</div>
                        </div>
                    </div>

                    <!-- Action Row - Copy Links -->
                    <div class="action-row">
                        <div class="action-item">
                            <label><i class="fas fa-barcode"></i> Code</label>
                            <div class="code-copy-compact">
                                <code class="code-display">{{ campaign.code }}</code>
                                <button class="btn-copy-compact" @click="copyCode(campaign.code)" title="Copy code">
                                    <i class="fas fa-copy"></i>
                                </button>
                            </div>
                        </div>
                        <div class="action-item">
                            <label><i class="fas fa-share-alt"></i> Referral Link</label>
                            <div class="link-copy-compact">
                                <input 
                                    type="text" 
                                    :value="getReferralLink(campaign.code)" 
                                    readonly 
                                    class="link-input-compact"
                                    @click="selectReferralLink"
                                />
                                <button class="btn-copy-compact" @click="copyReferralLink(campaign.code)" title="Copy link">
                                    <i class="fas fa-copy"></i>
                                </button>
                            </div>
                        </div>
                        <div v-if="campaign.target_link" class="action-item">
                            <label><i class="fas fa-link"></i> Target Link</label>
                            <a :href="campaign.target_link" target="_blank" class="link-external-compact">
                                <span class="link-text">{{ campaign.target_link }}</span>
                                <i class="fas fa-external-link-alt"></i>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabs -->
            <div class="tabs-card">
                <div class="tabs-header">
                    <div class="tab-items">
                        <button 
                            class="tab-item" 
                            :class="{ active: activeTab === 'members' }"
                            @click="activeTab = 'members'"
                        >
                            <i class="fas fa-users"></i>
                            <span>Members</span>
                            <span class="tab-count">{{ membersData?.total_members || 0 }}</span>
                        </button>
                        <button 
                            class="tab-item" 
                            :class="{ active: activeTab === 'events' }"
                            @click="activeTab = 'events'"
                        >
                            <i class="fas fa-chart-line"></i>
                            <span>Events</span>
                            <span class="tab-count">{{ filteredEvents.length }}</span>
                        </button>
                    </div>
                </div>
                
                <div class="tabs-body">
                    <!-- Members Tab -->
                    <div v-if="activeTab === 'members'">
                        <div class="tab-content-header">
                            <h6><i class="fas fa-users me-2"></i>Registered Members</h6>
                            <button class="btn-refresh" @click="loadMembers" title="Refresh members data">
                                <i class="fas fa-sync-alt"></i>
                            </button>
                        </div>

                        <div v-if="page.loadingMembers" class="loading-state-small">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p>Loading members...</p>
                        </div>

                        <div v-else-if="!membersData?.members || membersData.members.length === 0" class="empty-state">
                            <i class="fas fa-user-slash fa-3x"></i>
                            <h5>No Members Yet</h5>
                            <p>No members have registered via this campaign yet.</p>
                        </div>

                        <div v-else class="table-wrapper">
                            <o-table class="modern-table">
                                <thead>
                                    <tr>
                                        <th>No</th>
                                        <th>Member ID</th>
                                        <th>Member Name</th>
                                        <th>Entity Type</th>
                                        <th>Entity Name</th>
                                        <th>Registered At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(member, index) in membersData.members" :key="member.id">
                                        <td class="col-number">{{ index + 1 }}</td>
                                        <td class="col-member-id">
                                            <span class="member-id-badge">{{ member.member_id }}</span>
                                        </td>
                                        <td class="col-member-name">
                                            <div class="member-name-wrapper">
                                                <i class="fas fa-user-circle"></i>
                                                <span>{{ member.member_name }}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span class="badge-entity">{{ member.entity_type }}</span>
                                        </td>
                                        <td class="col-entity">{{ member.entity_name || '-' }}</td>
                                        <td class="col-date">{{ formatDateTime(member.registered_at) }}</td>
                                    </tr>
                                </tbody>
                            </o-table>
                        </div>
                    </div>

                    <!-- Events Tab -->
                    <div v-if="activeTab === 'events'">
                        <div class="tab-content-header">
                            <h6><i class="fas fa-chart-line me-2"></i>Campaign Events & Activities</h6>
                            <button class="btn-refresh" @click="loadEvents" title="Refresh events data">
                                <i class="fas fa-sync-alt"></i>
                            </button>
                        </div>

                        <!-- Event Summary Stats -->
                        <div v-if="filteredEvents.length > 0" class="stats-grid">
                            <div class="stat-card stat-views">
                                <div class="stat-icon">
                                    <i class="fas fa-eye"></i>
                                </div>
                                <div class="stat-content">
                                    <div class="stat-value">{{ getEventCount('view') }}</div>
                                    <div class="stat-label">Views</div>
                                </div>
                            </div>
                            <div class="stat-card stat-registrations">
                                <div class="stat-icon">
                                    <i class="fas fa-user-plus"></i>
                                </div>
                                <div class="stat-content">
                                    <div class="stat-value">{{ getEventCount('register') }}</div>
                                    <div class="stat-label">Registrations</div>
                                </div>
                            </div>
                            <div class="stat-card stat-conversion">
                                <div class="stat-icon">
                                    <i class="fas fa-chart-line"></i>
                                </div>
                                <div class="stat-content">
                                    <div class="stat-value">{{ getConversionRate() }}%</div>
                                    <div class="stat-label">Conversion Rate</div>
                                </div>
                            </div>
                            <div class="stat-card stat-total">
                                <div class="stat-icon">
                                    <i class="fas fa-calendar-check"></i>
                                </div>
                                <div class="stat-content">
                                    <div class="stat-value">{{ filteredEvents.length }}</div>
                                    <div class="stat-label">Active Events</div>
                                </div>
                            </div>
                        </div>

                        <div v-if="page.loadingEvents" class="loading-state-small">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p>Loading events...</p>
                        </div>

                        <div v-else-if="!filteredEvents || filteredEvents.length === 0" class="empty-state">
                            <i class="fas fa-calendar-times fa-3x"></i>
                            <h5>No Events Yet</h5>
                            <p>No events have been tracked for this campaign yet.</p>
                        </div>

                        <div v-else class="table-wrapper">
                            <o-table class="modern-table">
                                <thead>
                                    <tr>
                                        <th>No</th>
                                        <th>Event Type</th>
                                        <th>Campaign Status</th>
                                        <th>Member</th>
                                        <th>Business</th>
                                        <th>IP Address</th>
                                        <th>Date & Time</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(event, index) in filteredEvents" :key="event.id">
                                        <td class="col-number">{{ index + 1 }}</td>
                                        <td>
                                            <span 
                                                class="event-badge"
                                                :class="getEventTypeBadge(event.event_type)"
                                            >
                                                <i :class="getEventTypeIcon(event.event_type)"></i>
                                                <span>{{ event.event_type }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <span 
                                                v-if="event.campaign_status_at_event"
                                                class="status-badge-small" 
                                                :class="'status-' + event.campaign_status_at_event"
                                            >
                                                {{ getCampaignStatusLabel(event.campaign_status_at_event) }}
                                            </span>
                                            <span v-else class="text-muted">-</span>
                                        </td>
                                        <td class="col-member">
                                            <div class="member-wrapper">
                                                <i v-if="event.member_name === 'Anonymous'" class="fas fa-user-secret"></i>
                                                <i v-else class="fas fa-user-circle"></i>
                                                <span>{{ event.member_name }}</span>
                                            </div>
                                        </td>
                                        <td class="col-business">
                                            <div class="business-type">{{ event.business_type }}</div>
                                            <div class="business-name">{{ event.business_name || '-' }}</div>
                                        </td>
                                        <td class="col-ip">
                                            <code class="ip-address">{{ event.event_payload?.ip_address || '-' }}</code>
                                        </td>
                                        <td class="col-datetime">{{ formatDateTime(event.created_at) }}</td>
                                        <td class="col-actions">
                                            <button 
                                                v-if="event.event_payload"
                                                class="btn-action btn-action-view"
                                                @click="viewEventPayload(event)"
                                                title="View details"
                                            >
                                                <i class="fas fa-eye"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </o-table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Event Payload Modal -->
        <o-modal v-if="isPayloadModalVisible" @close="closePayloadModal" modal-class="show d-block">
            <template v-slot:header>
                <h5 class="modal-title">
                    <i class="fas fa-info-circle me-2 text-primary"></i>Event Details
                </h5>
            </template>
            <template v-slot:body>
                <div v-if="selectedEvent" class="event-payload-details">
                    <div class="payload-row">
                        <label>Event ID</label>
                        <span class="payload-value">{{ selectedEvent.event_id }}</span>
                    </div>
                    <div class="payload-row">
                        <label>Member</label>
                        <span class="payload-value">{{ selectedEvent.member_name }}</span>
                    </div>
                    <div class="payload-row">
                        <label>Event Payload</label>
                        <pre class="payload-json">{{ JSON.stringify(selectedEvent.event_payload, null, 2) }}</pre>
                    </div>
                </div>
            </template>
        </o-modal>
        </template>
    </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { OBreadCrumb, OTable, OModal } from '@/components/OmnihControl'
import api from '@/helpers/api'
import { useToast } from '@/composables/useToast'

export default {
    name: 'CampaignDetail',
    components: {
        OBreadCrumb,
        OTable,
        OModal
    },
    setup() {
        const router = useRouter()
        const route = useRoute()
        const store = useStore()
        const { showToast } = useToast()

        // Check if marketing module is enabled via feature flag
        const isMarketingModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.marketingModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        const campaignId = ref(route.params.id)
        const campaign = ref(null)
        const membersData = ref(null)
        const eventsData = ref(null)
        const activeTab = ref('members')
        
        const page = reactive({
            isLoading: false,
            loadingMembers: false,
            loadingEvents: false
        })

        const validation = ref('')
        const isPayloadModalVisible = ref(false)
        const selectedEvent = ref(null)

        // Computed: Filtered events based on campaign status
        const filteredEvents = computed(() => {
            if (!eventsData.value?.events) return []
            return eventsData.value.events.filter(event => 
                event.campaign_is_active_at_event === true || 
                event.campaign_status_at_event === 'active'
            )
        })

        onMounted(() => {
            loadCampaignDetail()
            loadMembers()
            loadEvents()
        })


        function loadCampaignDetail() {
            page.isLoading = true
            validation.value = ''

            api.request('get', `/api/campaign/${campaignId.value}`)
                .then((response) => {
                    campaign.value = response.data.data
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to load campaign detail'
                })
                .finally(() => {
                    page.isLoading = false
                })
        }

        function loadMembers() {
            page.loadingMembers = true

            api.request('get', `/api/campaign/${campaignId.value}/members`)
                .then((response) => {
                    membersData.value = response.data.data
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to load members'
                })
                .finally(() => {
                    page.loadingMembers = false
                })
        }

        function loadEvents() {
            page.loadingEvents = true

            api.request('get', `/api/campaign/${campaignId.value}/events`)
                .then((response) => {
                    eventsData.value = response.data.data
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to load events'
                })
                .finally(() => {
                    page.loadingEvents = false
                })
        }

        function viewEventPayload(event) {
            selectedEvent.value = event
            isPayloadModalVisible.value = true
        }

        function closePayloadModal() {
            isPayloadModalVisible.value = false
            selectedEvent.value = null
        }

        function getBusinessTypeName(type) {
            if (!type) return '-'
            return type.replace('App\\Models\\', '')
        }

        function formatDate(date) {
            if (!date) return '-'
            return new Date(date).toLocaleDateString('en-GB')
        }

        function formatDateTime(date) {
            if (!date) return '-'
            return new Date(date).toLocaleString('en-GB')
        }

        function copyCode(code) {
            navigator.clipboard.writeText(code).then(() => {
                showToast({
                    type: 'success',
                    title: 'Copied!',
                    message: 'Referral code copied to clipboard.'
                })
            })
        }

        function getReferralLink(code) {
            const currentDomain = window.location.origin
            return `${currentDomain}/marketing?referral=${code}`
        }

        function copyReferralLink(code) {
            const link = getReferralLink(code)
            navigator.clipboard.writeText(link).then(() => {
                showToast({
                    type: 'success',
                    title: 'Copied!',
                    message: 'Referral link copied to clipboard.'
                })
            })
        }

        function selectReferralLink(event) {
            event.target.select()
        }

        function goBack() {
            router.go(-1)
        }

        function getEventTypeBadge() {
            return ''
        }

        function getEventTypeIcon(eventType) {
            const icons = {
                'view': 'fas fa-eye',
                'register': 'fas fa-user-plus',
                'click': 'fas fa-mouse-pointer',
                'conversion': 'fas fa-check-circle'
            }
            return icons[eventType] || 'fas fa-circle'
        }

        function getEventCount(eventType) {
            if (!filteredEvents.value) return 0
            return filteredEvents.value.filter(e => e.event_type === eventType).length
        }

        function getConversionRate() {
            const views = getEventCount('view')
            const registers = getEventCount('register')
            if (views === 0) return 0
            return ((registers / views) * 100).toFixed(1)
        }

        function getCampaignStatusLabel(status) {
            const labels = {
                'active': 'Active',
                'expired': 'Expired',
                'upcoming': 'Upcoming'
            }
            return labels[status] || status
        }

        // Watch tab changes to load data
        const unwatchTab = () => {}
        const watchTab = () => {
            return () => {
                if (activeTab.value === 'members' && !membersData.value) {
                    loadMembers()
                } else if (activeTab.value === 'events' && !eventsData.value) {
                    loadEvents()
                }
            }
        }

        onMounted(() => {
            const stopWatch = watchTab()
            unwatchTab.value = stopWatch
        })

        return {
            campaignId,
            campaign,
            membersData,
            eventsData,
            activeTab,
            filteredEvents,
            page,
            validation,
            isPayloadModalVisible,
            selectedEvent,
            loadMembers,
            loadEvents,
            viewEventPayload,
            closePayloadModal,
            getBusinessTypeName,
            formatDate,
            formatDateTime,
            copyCode,
            getReferralLink,
            copyReferralLink,
            selectReferralLink,
            goBack,
            getEventTypeBadge,
            getEventTypeIcon,
            getEventCount,
            getConversionRate,
            getCampaignStatusLabel,
            isMarketingModuleEnabled
        }
    }
}
</script>

<style scoped>
/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

/* Container */
.campaign-detail-container {
    padding: 12px;
    padding-right: 20px;
    max-width: 100%;
}

/* Button Back */
.btn-back {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-back:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

/* Alert */
.alert-danger {
    background: #fee2e2;
    color: #991b1b;
    border-radius: 8px;
    border: none;
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;
}

/* Loading */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
}

.loading-text {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 0.875rem;
}

.loading-state-small {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
}

.loading-state-small p {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 0.875rem;
}

/* Detail Content */
.detail-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Info Card */
.info-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
}

.info-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.info-header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.info-header-left i {
    color: var(--primary-color, #3b82f6);
    font-size: 1.25rem;
}

.info-header h5 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
}

.info-body {
    padding: 1.75rem 2rem;
}

/* Compact Grid - 3 Columns */
.info-grid-compact {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #f3f4f6;
}

.info-item-compact {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}

.info-item-compact label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #9ca3af;
    margin: 0;
}

.info-item-compact label i {
    color: var(--primary-color, #3b82f6);
    font-size: 0.875rem;
}

.info-value-compact {
    font-size: 0.9375rem;
    color: #111827;
    font-weight: 500;
    line-height: 1.5;
}

.badge-type-compact {
    display: inline-block;
    padding: 0.375rem 0.75rem;
    background: #eff6ff;
    color: #3b82f6;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    align-self: flex-start;
}

/* Action Row - Horizontal Layout */
.action-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    padding-top: 1.5rem;
}

.action-item {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    min-width: 0;
}

.action-item label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #9ca3af;
    margin: 0;
    white-space: nowrap;
}

.action-item label i {
    color: var(--primary-color, #3b82f6);
    font-size: 0.875rem;
}

/* Code Copy Compact */
.code-copy-compact {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: 44px;
}

.code-display {
    flex: 1;
    background: #f9fafb;
    color: #374151;
    padding: 0 0.875rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-family: 'Monaco', 'Courier New', monospace;
    font-weight: 600;
    border: 1px solid #e5e7eb;
    height: 100%;
    display: flex;
    align-items: center;
}

.btn-copy-compact {
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    width: 44px;
    height: 44px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.875rem;
}

.btn-copy-compact:hover {
    background: var(--primary-color-dark, #2563eb);
}

/* Link Copy Compact */
.link-copy-compact {
    display: flex;
    gap: 0.5rem;
    height: 44px;
    align-items: center;
}

.link-input-compact {
    flex: 1;
    padding: 0 0.875rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    color: #374151;
    font-size: 0.875rem;
    font-family: 'Monaco', 'Courier New', monospace;
    outline: none;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    height: 100%;
}

.link-input-compact:focus {
    border-color: var(--primary-color, #3b82f6);
    outline: 2px solid rgba(59, 130, 246, 0.1);
}

/* Link External Compact */
.link-external-compact {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0 0.875rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    color: var(--primary-color, #3b82f6);
    text-decoration: none;
    font-size: 0.875rem;
    transition: all 0.2s ease;
    height: 44px;
}

.link-external-compact:hover {
    border-color: var(--primary-color, #3b82f6);
    background: #eff6ff;
}

.link-external-compact .link-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.link-external-compact i {
    flex-shrink: 0;
    font-size: 0.8125rem;
}

/* Link Copy Wrapper */
.link-copy-wrapper {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

.referral-link-input {
    flex: 1;
    border: none;
    background: white;
    color: #374151;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    font-size: 0.8125rem;
    font-family: 'Monaco', 'Courier New', monospace;
    outline: none;
}

.referral-link-input:focus {
    outline: 2px solid var(--primary-color, #3b82f6);
    outline-offset: -2px;
}

.btn-copy-link-small {
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    white-space: nowrap;
}

.btn-copy-link-small:hover {
    background: var(--primary-color-dark, #2563eb);
    transform: translateY(-1px);
}

.link-external {
    background: var(--primary-color-dark, #2563eb);
}

.link-external {
    color: var(--primary-color, #3b82f6);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    word-break: break-all;
}

.link-external:hover {
    text-decoration: underline;
}

.link-external i {
    flex-shrink: 0;
    font-size: 0.75rem;
}

/* Tabs Card */
.tabs-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
}

.tabs-header {
    border-bottom: 1px solid #e5e7eb;
}

.tab-items {
    display: flex;
    gap: 0;
}

.tab-item {
    flex: 1;
    background: transparent;
    border: none;
    padding: 1rem 1.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: #6b7280;
    font-size: 0.9375rem;
    font-weight: 500;
    border-bottom: 3px solid transparent;
}

.tab-item:hover {
    background: #f9fafb;
    color: #374151;
}

.tab-item.active {
    color: var(--primary-color, #3b82f6);
    border-bottom-color: var(--primary-color, #3b82f6);
    background: #f9fafb;
}

.tab-count {
    background: #e5e7eb;
    color: #6b7280;
    padding: 0.125rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.tab-item.active .tab-count {
    background: var(--primary-color, #3b82f6);
    color: white;
}

/* Tabs Body */
.tabs-body {
    padding: 1.5rem;
}

.tab-content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.tab-content-header h6 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    display: flex;
    align-items: center;
}

/* Event Filter Tabs */
.event-filter-tabs {
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    padding: 0.5rem;
    background: #f9fafb;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
}

.filter-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #6b7280;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.filter-tab:hover {
    background: #f9fafb;
    border-color: var(--primary-color, #3b82f6);
    color: var(--primary-color, #3b82f6);
}

.filter-tab.active {
    background: var(--primary-color, #3b82f6);
    border-color: var(--primary-color, #3b82f6);
    color: white;
}

.filter-tab .badge {
    background: rgba(0, 0, 0, 0.1);
    color: inherit;
    padding: 0.125rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    min-width: 24px;
    text-align: center;
}

.filter-tab.active .badge {
    background: rgba(255, 255, 255, 0.2);
}

.filter-tab .badge-success {
    background: rgba(16, 185, 129, 0.15);
    color: #059669;
}

.filter-tab.active .badge-success {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.filter-tab .badge-danger {
    background: rgba(239, 68, 68, 0.15);
    color: #dc2626;
}

.filter-tab.active .badge-danger {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.btn-refresh {
    background: white;
    color: var(--primary-color, #3b82f6);
    border: 1px solid #e5e7eb;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-refresh:hover {
    background: #f9fafb;
    border-color: var(--primary-color, #3b82f6);
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.stat-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: all 0.2s ease;
}

.stat-card:hover {
    border-color: var(--primary-color, #3b82f6);

}

.stat-views .stat-icon {
    background: #eff6ff;
    color: var(--primary-color, #3b82f6);
}

.stat-registrations .stat-icon {
    background: #eff6ff;
    color: var(--primary-color, #3b82f6);
}

.stat-conversion .stat-icon {
    background: #eff6ff;
    color: var(--primary-color, #3b82f6);
}

.stat-total .stat-icon {
    background: #f3f4f6;
    color: #6b7280;
}

.stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    flex-shrink: 0;
}

.stat-content {
    flex: 1;
}

.stat-value {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1;
    margin-bottom: 0.375rem;
    color: #111827;
}

.stat-label {
    font-size: 0.8125rem;
    color: #6b7280;
    font-weight: 500;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: #9ca3af;
}

.empty-state i {
    margin-bottom: 1rem;
    opacity: 0.5;
}

.empty-state h5 {
    color: #374151;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.empty-state p {
    color: #6b7280;
    font-size: 0.875rem;
}

/* Table */
.table-wrapper {
    overflow-x: auto;
    margin-top: 1rem;
}

.modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.modern-table thead th {
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
}

.modern-table tbody tr {
    border-bottom: 1px solid #f3f4f6;
    transition: background-color 0.15s ease;
}

.modern-table tbody tr:hover {
    background-color: #f9fafb;
}

.modern-table tbody td {
    padding: 1rem;
    font-size: 0.875rem;
    color: #374151;
    vertical-align: top;
}

.col-number {
    width: 60px;
    color: #9ca3af;
    font-weight: 500;
}

.col-member-id {
    min-width: 120px;
}

.member-id-badge {
    display: inline-block;
    background: #f9fafb;
    color: #6b7280;
    padding: 0.25rem 0.625rem;
    border-radius: 6px;
    font-size: 0.8125rem;
    font-weight: 600;
    font-family: 'Monaco', 'Courier New', monospace;
}

.member-name-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.member-name-wrapper i {
    color: #9ca3af;
    font-size: 1rem;
}

.badge-entity {
    display: inline-block;
    padding: 0.25rem 0.625rem;
    background: #eff6ff;
    color: #3b82f6;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
}

.col-entity {
    color: #6b7280;
}

.col-date,
.col-datetime {
    color: #6b7280;
    font-size: 0.8125rem;
}

.event-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.875rem;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 500;
    background: #f9fafb;
    color: #6b7280;
    border: 1px solid rgba(229, 231, 235, 0.8);
    transition: all 0.2s ease;
}

.col-member {
    min-width: 140px;
}

.member-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.member-wrapper i {
    color: #9ca3af;
    font-size: 1rem;
}

.col-business {
    min-width: 160px;
}

.business-type {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-bottom: 0.25rem;
}

.business-name {
    color: #374151;
}

.col-ip {
    min-width: 120px;
}

.ip-address {
    background: #f9fafb;
    color: #6b7280;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-family: 'Monaco', 'Courier New', monospace;
}

/* Action Buttons */
.col-actions {
    text-align: center;
}

.btn-action {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
}

.btn-action-view {
    background: #eff6ff;
    color: var(--primary-color, #3b82f6);
}

.btn-action-view:hover {
    background: var(--primary-color, #3b82f6);
    color: white;
}

/* Event Payload Modal */
.event-payload-details {
    padding: 1rem 0;
}

.payload-row {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem 0;
    border-bottom: 1px solid #f3f4f6;
}

.payload-row:last-child {
    border-bottom: none;
}

.payload-row label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin: 0;
}

.payload-value {
    color: #374151;
    font-size: 0.9375rem;
}

.payload-json {
    background: #f9fafb;
    color: #374151;
    padding: 1rem;
    border-radius: 8px;
    font-size: 0.8125rem;
    line-height: 1.5;
    max-height: 400px;
    overflow-y: auto;
    font-family: 'Monaco', 'Courier New', monospace;
    margin: 0;
}

.text-primary {
    color: var(--primary-color, #F93E3E) !important;
}

/* Responsive */
@media (max-width: 1024px) {
    .info-grid-compact {
        grid-template-columns: repeat(2, 1fr);
    }

    .action-row {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .info-body {
        padding: 1.25rem;
    }

    .info-grid-compact {
        grid-template-columns: 1fr;
        gap: 1.25rem;
        padding-bottom: 1.25rem;
    }

    .action-row {
        grid-template-columns: 1fr;
        gap: 1rem;
        padding-top: 1.25rem;
    }

    .stats-grid {
        grid-template-columns: 1fr;
    }

    .tab-item {
        padding: 0.875rem 1rem;
        font-size: 0.875rem;
    }

    .tab-item span:not(.tab-count) {
        display: none;
    }

    .tab-content-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .btn-refresh {
        width: 100%;
        height: 36px;
    }

    .modern-table {
        font-size: 0.8125rem;
    }

    .modern-table thead th {
        padding: 0.5rem;
        font-size: 0.6875rem;
    }

    .modern-table tbody td {
        padding: 0.75rem 0.5rem;
    }
}

@media (max-width: 480px) {
    .info-header h5,
    .tab-content-header h6 {
        font-size: 1rem;
    }

    .stat-value {
        font-size: 1.5rem;
    }

    .stat-icon {
        width: 40px;
        height: 40px;
        font-size: 1.25rem;
    }
}

/* Status Badges */
.status-badge-small {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.status-active {
    background-color: #d1fae5;
    color: #065f46;
}

.status-expired {
    background-color: #fee2e2;
    color: #991b1b;
}

.status-upcoming {
    background-color: #dbeafe;
    color: #1e40af;
}
</style>
